import { askConfirm } from '@/components/ui/ConfirmHost';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Edit2, ClipboardList, Users } from 'lucide-react';
import { getExams, getQuestionPool, postPoolExam, putPoolExam, deleteExam, assignExam, errText, type ClassGroup, type ExamRow, type Cycle } from '@/api/admissions';
import { Card, Empty, Pill, Btn, Modal, Field, input, Notice, Toggle, fmtDate } from './ui';

export function ExamsTab({ cycle, groups }: { cycle: Cycle | undefined; groups: ClassGroup[] }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<ExamRow | 'new' | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const q = useQuery({ queryKey: ['adm-exams', cycle?.id], queryFn: () => getExams(cycle!.id), enabled: !!cycle });
  const exams = q.data ?? [];
  const covered = new Set(exams.flatMap((e) => e.class_names.map((n) => n.replace(/\W/g, '').toUpperCase())));
  const missing = groups.filter((g) => !covered.has(g.key));

  const m = useMutation({
    mutationFn: (fn: () => Promise<any>) => fn(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['adm-exams'] }); qc.invalidateQueries({ queryKey: ['adm-apps'] }); },
    onError: (e) => setNotice({ tone: 'err', text: errText(e) }),
  });

  if (!cycle) return <Empty icon={ClipboardList}>Create an admission cycle first (Settings tab).</Empty>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-slate-600">One exam per class. Every applicant gets their own random questions from your question banks, marked automatically. Paid applicants are booked in straight away.</p>
        <Btn kind="dark" onClick={() => setEdit('new')} className="shrink-0"><Plus className="w-4 h-4" />New entrance exam</Btn>
      </div>
      {notice && <Notice tone={notice.tone} onClose={() => setNotice(null)}>{notice.text}</Notice>}
      {missing.length > 0 && exams.length > 0 && <Notice tone="info">No exam yet for: {missing.map((g) => g.name).join(', ')}. Applicants to those classes will not be booked automatically.</Notice>}

      {q.isLoading ? <p className="text-sm text-slate-500">Loading…</p> : exams.length === 0 ? (
        <Card><Empty icon={ClipboardList}>No entrance exams yet. Create one for each class you admit into (for example JSS1 and SS1).</Empty></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {exams.map((e) => (
            <Card key={e.id} title={e.name} sub={<>{e.class_names.join(', ') || 'All classes'} · {e.mode === 'written' ? 'Written' : `${e.question_count} questions · ${e.duration_minutes} min`} · pass {e.pass_mark}%</>}
              action={<div className="flex gap-1">
                <button onClick={() => setEdit(e)} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Edit"><Edit2 className="w-4 h-4" /></button>
                <button onClick={async () => { if (await askConfirm(`Delete ${e.name}?`)) m.mutate(() => deleteExam(e.id)); }} className="p-1.5 text-slate-400 hover:text-red-600" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
              </div>}>
              {e.subjects.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">{e.subjects.map((s) => <Pill key={s.subject_id} tone="blue">{s.name} · {s.question_count}</Pill>)}</div>
              )}
              <div className="grid grid-cols-4 gap-2 text-center">
                {[['Booked', e.candidates], ['Taken', e.taken], ['Passed', e.passed], ['Average', e.average != null ? `${e.average}%` : '—']].map(([k, v]) => (
                  <div key={k as string} className="bg-slate-50 rounded-lg py-2"><p className="text-lg font-extrabold text-slate-900">{v}</p><p className="text-[10px] uppercase font-bold text-slate-500">{k}</p></div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                <span>{e.scheduled_at ? `Opens ${fmtDate(e.scheduled_at, true)}` : 'Open as soon as booked'}{e.venue ? ` · ${e.venue}` : ''}</span>
                <button onClick={() => m.mutate(async () => { const r: any = await assignExam(e.id); setNotice({ tone: 'ok', text: `${r?.assigned ?? 0} waiting applicant(s) booked.` }); })} className="font-semibold text-green-700 inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />Book waiting applicants</button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {edit && <ExamEditor cycle={cycle} groups={groups} exam={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={(msg) => { setEdit(null); setNotice({ tone: 'ok', text: msg }); qc.invalidateQueries({ queryKey: ['adm-exams'] }); }} />}
    </div>
  );
}

function ExamEditor({ cycle, groups, exam, onClose, onSaved }: { cycle: Cycle; groups: ClassGroup[]; exam: ExamRow | null; onClose: () => void; onSaved: (msg: string) => void }) {
  const [name, setName] = useState(exam?.name ?? '');
  const [classes, setClasses] = useState<string[]>(exam?.class_names ?? []);
  const [mode, setMode] = useState<'cbt' | 'written'>(exam?.mode ?? 'cbt');
  const [counts, setCounts] = useState<Record<number, number>>(() => Object.fromEntries((exam?.subjects ?? []).map((s) => [s.subject_id, s.question_count])));
  const [duration, setDuration] = useState(exam?.duration_minutes ?? 60);
  const [pass, setPass] = useState(exam?.pass_mark ?? 50);
  const [when, setWhen] = useState(exam?.scheduled_at ? exam.scheduled_at.slice(0, 16) : '');
  const [venue, setVenue] = useState(exam?.venue ?? '');
  const [instructions, setInstructions] = useState(exam?.instructions ?? '');
  const [auto, setAuto] = useState(exam?.auto_assign ?? true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (!name && classes.length) setName(`${classes.join(' / ')} entrance exam`); }, [classes]); // eslint-disable-line react-hooks/exhaustive-deps

  const poolQ = useQuery({ queryKey: ['adm-pool', classes.join(',')], queryFn: () => getQuestionPool(classes), enabled: mode === 'cbt' });
  const pool = poolQ.data ?? [];
  const chosen = Object.entries(counts).filter(([, n]) => n > 0).map(([id, n]) => ({ subject_id: Number(id), question_count: n }));
  const totalQ = chosen.reduce((s, x) => s + x.question_count, 0);
  const over = pool.filter((p) => (counts[p.subject_id] ?? 0) > p.available);

  const save = useMutation({
    mutationFn: () => {
      const body: any = { admission_cycle_id: cycle.id, name, mode, class_names: classes, pass_mark: pass, scheduled_at: when ? new Date(when).toISOString() : null, venue: venue || null, instructions: instructions || null, auto_assign: auto };
      if (mode === 'cbt') { body.subjects = chosen; body.duration_minutes = duration; }
      return exam ? putPoolExam(exam.id, body) : postPoolExam(body);
    },
    onSuccess: (r: any) => onSaved(exam ? 'Exam saved.' : `Exam created${r?.assigned_now ? `, ${r.assigned_now} waiting applicant(s) booked` : ''}.`),
    onError: (e) => setErr(errText(e)),
  });
  const valid = name && classes.length > 0 && (mode === 'written' || (chosen.length > 0 && over.length === 0));

  return (
    <Modal wide title={exam ? 'Edit entrance exam' : 'New entrance exam'} onClose={onClose}
      footer={<><Btn kind="ghost" onClick={onClose}>Cancel</Btn><Btn disabled={!valid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save exam'}</Btn></>}>
      <div className="space-y-4">
        <Field label="Classes this exam is for" hint="Applicants apply to the class, not the arm. SS1 covers every SS1 arm.">
          <div className="flex flex-wrap gap-2">
            {groups.map((g) => {
              const on = classes.includes(g.name);
              return <button type="button" key={g.key} onClick={() => setClasses(on ? classes.filter((c) => c !== g.name) : [...classes, g.name])}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${on ? 'bg-green-600 text-white border-green-600' : 'bg-white text-slate-700 border-slate-300'}`}>{g.name}</button>;
            })}
          </div>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Exam name"><input value={name} onChange={(e) => setName(e.target.value)} className={input} placeholder="SS1 entrance exam" /></Field>
          <Field label="Type">
            <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-lg p-1">
              {(['cbt', 'written'] as const).map((k) => <button type="button" key={k} onClick={() => setMode(k)} className={`py-1.5 rounded-md text-sm font-semibold ${mode === k ? 'bg-white shadow-sm' : 'text-slate-600'}`}>{k === 'cbt' ? 'CBT (auto-marked)' : 'Written'}</button>)}
            </div>
          </Field>
        </div>

        {mode === 'cbt' && (
          <Field label={`Subjects and number of questions (${totalQ} total)`} hint="Questions come from your Question Banks for each subject. Only auto-markable types (objective, true/false, short answer) are used.">
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {poolQ.isLoading ? <p className="p-3 text-sm text-slate-500">Loading question banks…</p> : pool.length === 0 ? <p className="p-3 text-sm text-slate-500">No subjects found.</p> : pool.map((p) => {
                const n = counts[p.subject_id] ?? 0;
                return (
                  <div key={p.subject_id} className="flex items-center gap-3 px-3 py-2">
                    <span className="flex-1 min-w-0"><span className="block text-sm font-medium text-slate-900">{p.name}</span><span className={`block text-xs ${p.available === 0 ? 'text-slate-400' : 'text-slate-500'}`}>{p.available} question{p.available === 1 ? '' : 's'} available</span></span>
                    <input type="number" min={0} max={p.available} value={n || ''} placeholder="0" disabled={p.available === 0}
                      onChange={(e) => setCounts({ ...counts, [p.subject_id]: Math.max(0, Number(e.target.value) || 0) })}
                      className={`w-20 border rounded-md px-2 py-1.5 text-sm text-right ${n > p.available ? 'border-red-400 text-red-600' : 'border-slate-300'}`} />
                  </div>
                );
              })}
            </div>
            {over.length > 0 && <span className="block text-xs text-red-600 mt-1">Not enough questions in: {over.map((o) => o.name).join(', ')}.</span>}
          </Field>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {mode === 'cbt' && <Field label="Duration (min)"><input type="number" min={5} max={300} value={duration} onChange={(e) => setDuration(Number(e.target.value))} className={input} /></Field>}
          <Field label="Pass mark (%)"><input type="number" min={0} max={100} value={pass} onChange={(e) => setPass(Number(e.target.value))} className={input} /></Field>
          <Field label="Opens at (optional)"><input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className={input} /></Field>
          <Field label="Venue (optional)"><input value={venue} onChange={(e) => setVenue(e.target.value)} className={input} placeholder="Online or Hall B" /></Field>
        </div>
        <Field label="Instructions for applicants (optional)"><textarea rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} className={input} placeholder="Answer all questions. Calculators are not allowed." /></Field>
        <Toggle checked={auto} onChange={setAuto} label="Book applicants automatically" hint="As soon as an applicant to these classes pays, they are booked into this exam." />
        {err && <Notice tone="err">{err}</Notice>}
      </div>
    </Modal>
  );
}

export function useGroupNames(groups: ClassGroup[]) {
  return useMemo(() => groups.map((g) => g.name), [groups]);
}
